import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  Alert,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Import step components
import AccountCreationStep from './steps/AccountCreationStep';
import BusinessVerificationStep from './steps/BusinessVerificationStep';
import StripeConnectStep from './steps/StripeConnectStep';
import VenueDetailsStep from './steps/VenueDetailsStep';
import CompleteSetupStep from './steps/CompleteSetupStep';

// Import services
import { createVenueAccount } from '../../../services/accountService';
import { useUser } from '../../../contexts/UserContext';

// Define steps
const steps = [
  'Create Account',
  'Business Verification',
  'Connect Payments',
  'Venue Details',
  'Complete Setup'
];

// Initial data state
const initialAccountData = {
  // Account info
  email: '',
  password: '',
  name: '',
  phone: '',
  accountType: 'business', // 'business' or 'individual'
  acceptTerms: false,
  
  // Business verification
  businessName: '',
  businessType: 'restaurant', // Default, will be selectable
  businessAddress: {
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'US'
  },
  taxId: '',
  verificationStatus: 'pending',
  
  // Stripe Connect
  stripeConnected: false,
  stripeAccountId: '',
  payoutInfo: {
    accountNumber: '',
    routingNumber: '',
    accountType: 'checking'
  },
  commissionRate: 5, // Default 5% platform fee
  
  // Venue details (will link to existing venue creation flow)
  venueId: '',
  venueName: '',
  venueDescription: '',
  venueType: 'restaurant',
  venueLocation: null,
  venueImages: [],
  
  // Setup completion
  setupCompleted: false,
  createdAt: null,
  updatedAt: null
};

const VenueSignupFlow: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser, isLoading: userLoading } = useUser();
  
  const [activeStep, setActiveStep] = useState(0);
  const [accountData, setAccountData] = useState(initialAccountData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // If user is already logged in, prefill the account data
  useEffect(() => {
    if (currentUser) {
      setAccountData(prev => ({
        ...prev,
        email: currentUser.email || '',
        name: currentUser.displayName || ''
      }));
    }
  }, [currentUser]);
  
  // Handle data changes from step components
  const handleDataChange = (data: Partial<typeof accountData>) => {
    setAccountData(prev => ({
      ...prev,
      ...data
    }));
    
    // Clear errors when user makes changes
    if (error) setError(null);
  };
  
  // Step navigation
  const handleNext = () => {
    if (validateCurrentStep()) {
      setActiveStep(prevStep => prevStep + 1);
    }
  };
  
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };
  
  // Validate current step
  const validateCurrentStep = (): boolean => {
    setError(null);
    
    // Validate common fields
    if (activeStep === 0 && !accountData.acceptTerms) {
      setError('You must accept the terms and conditions to continue');
      return false;
    }
    
    switch (activeStep) {
      case 0: // Account Creation
        if (!currentUser) {
          if (!accountData.email || !accountData.password) {
            setError('Email and password are required');
            return false;
          }
          
          // Validate email format
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(accountData.email)) {
            setError('Please enter a valid email address');
            return false;
          }
          
          // Validate password strength
          if (accountData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            return false;
          }
        }
        
        if (!accountData.name) {
          setError('Name is required');
          return false;
        }
        break;
        
      case 1: // Business Verification
        if (!accountData.businessName) {
          setError('Business name is required');
          return false;
        }
        
        const { street, city, state, zip } = accountData.businessAddress;
        if (!street || !city || !state || !zip) {
          setError('Business address is required');
          return false;
        }
        break;
        
      case 2: // Stripe Connect
        // Stripe connection is optional during signup, can be completed later
        break;
        
      case 3: // Venue Details
        if (!accountData.venueName || !accountData.venueDescription) {
          setError('Venue name and description are required');
          return false;
        }
        break;
    }
    
    return true;
  };
  
  // Final submission
  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Prepare the data - trim email and ensure proper formatting
      const submissionData = {
        ...accountData,
        email: accountData.email?.trim().toLowerCase(),
        userId: currentUser?.uid // Add the current user ID if they're logged in
      };
      
      // Create venue account
      const result = await createVenueAccount(submissionData);
      
      // Set success
      setSuccess('Your venue account has been created successfully!');
      
      // Update account data with the result
      setAccountData(prev => ({
        ...prev,
        ...result,
        setupCompleted: true
      }));
      
      // Navigate directly to venue dashboard - using direct window location for more reliable redirect
      setTimeout(() => {
        window.location.href = '/venues';
      }, 2000);
    } catch (err: any) {
      console.error('Error creating venue account:', err);
      setError(err.message || 'Failed to create venue account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Render current step
  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <AccountCreationStep
            data={accountData}
            onChange={handleDataChange}
            isLoggedIn={!!currentUser}
          />
        );
      case 1:
        return (
          <BusinessVerificationStep
            data={accountData}
            onChange={handleDataChange}
          />
        );
      case 2:
        return (
          <StripeConnectStep
            data={accountData}
            onChange={handleDataChange}
          />
        );
      case 3:
        return (
          <VenueDetailsStep
            data={accountData}
            onChange={handleDataChange}
          />
        );
      case 4:
        return (
          <CompleteSetupStep
            data={accountData}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };
  
  if (userLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Create Venue Account
          </Typography>
        </Box>
        
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success" 
            icon={<CheckCircleIcon fontSize="inherit" />}
            sx={{ mb: 3 }}
          >
            {success}
          </Alert>
        )}
        
        <Box sx={{ mb: 4 }}>
          {renderStep()}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0 || isSubmitting}
          >
            Back
          </Button>
          
          <Button
            variant="contained"
            onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {activeStep === steps.length - 1 ? 'Create Venue Account' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default VenueSignupFlow;
